import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Check, CreditCard, QrCode, Wallet, Smartphone } from "lucide-react";
import { useI18n } from "../lib/LanguageContext";
import { formatIDR } from "../lib/translations";
import VoucherInput from "../components/VoucherInput";
import { useMidtrans } from "../lib/useMidtrans";

const METHODS = [
  { v: "card", key: "card", icon: CreditCard, real: true },
  { v: "qris", key: "qris", icon: QrCode, real: false },
  { v: "gopay", key: "gopay", icon: Wallet, real: false },
  { v: "shopeepay", key: "shopeepay", icon: Smartphone, real: false },
];

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const { user } = useAuth(); const nav = useNavigate();
  const [loadingId, setLoadingId] = useState("");
  const [method, setMethod] = useState("card");
  const [voucher, setVoucher] = useState(null); // {code, discount}
  const { t } = useI18n();
  const { midtrans, paySnap } = useMidtrans();
  useEffect(() => { api.get("/plans").then(r => setPlans(r.data)); }, []);

  const buy = async (id) => {
    if (!user) { toast.error(t("pricing_extra.login_first")); nav("/masuk"); return; }
    setLoadingId(id);
    try {
      // Midtrans real path for QRIS/Gopay/ShopeePay when configured
      if (midtrans.configured && ["qris","gopay","shopeepay"].includes(method)) {
        await paySnap({
          plan_id: id,
          onSuccess: () => { toast.success(t("pricing_extra.payment_success")); nav("/nuna"); },
          onPending: () => toast.info(t("pricing_extra.pending")),
          onError: () => toast.error(t("pricing_extra.payment_failed")),
          onClose: () => setLoadingId(""),
        });
        return;
      }
      const r = await api.post("/payments/checkout", { plan_id: id, origin_url: window.location.origin, payment_method: method, voucher_code: voucher?.code });
      window.location.href = r.data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || err.message || t("pricing_extra.checkout_failed")); setLoadingId(""); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="pricing-page">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-2 text-center">{t("pricing.badge")}</div>
      <h1 className="font-display text-4xl md:text-5xl font-medium text-center mb-3">{t("pricing.title")}</h1>
      <p className="text-center text-muted-foreground max-w-xl mx-auto mb-10">{t("pricing.subtitle")}</p>

      <Card className="rounded-2xl p-5 mb-8 border-teal/15" data-testid="payment-method-selector">
        <div className="text-xs uppercase tracking-widest text-teal font-bold mb-3">{t("pricing.pay_method")}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {METHODS.map(m => {
            const Icon = m.icon; const active = method === m.v;
            return (
              <button key={m.v} onClick={()=>setMethod(m.v)} className={`border rounded-lg p-3 flex items-center gap-2 ${active ? "border-teal bg-teal-soft ring-1 ring-teal" : "border-teal/15 hover:border-teal/30"}`} data-testid={`pay-method-${m.v}`}>
                <Icon className={`w-4 h-4 ${active ? "text-teal" : "text-muted-foreground"}`}/>
                <span className="text-sm font-medium text-left flex-1">{m.key === "card" ? t("pricing_extra.card") : m.key === "qris" ? "QRIS" : m.key === "gopay" ? "GoPay" : "ShopeePay"}</span>
                {!m.real && <span className="text-[9px] uppercase tracking-widest text-amber-600 font-bold bg-amber-50 border border-amber-200 rounded px-1">{t("pricing_extra.mock")}</span>}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 italic">
          {midtrans.configured ? `✓ ${t("pricing_extra.midtrans")} (${midtrans.is_production ? t("pricing_extra.production") : t("pricing_extra.sandbox")}) — QRIS/GoPay/ShopeePay Snap.` : t("pricing.note_stripe")}
        </p>
      </Card>

      <Card className="rounded-2xl p-5 mb-8 border-teal/15 max-w-md">
        <div className="text-xs uppercase tracking-widest text-teal font-bold mb-3">{t("pricing_extra.voucher")}</div>
        <VoucherInput amount={100000} scope="subscription" onApply={setVoucher}/>
      </Card>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map(p => {
          const yearly = p.id.includes("yearly");
          const tierLabel = t(`pricing_extra.tiers.${p.tier}`);
          const planLabel = `${tierLabel} — ${t(`pricing_extra.plan_periods.${yearly ? "yearly" : "monthly"}`)}`;
          const discountAmt = voucher ? Math.min(voucher.discount, p.amount) : 0;
          const finalAmt = p.amount - discountAmt;
          return (
            <Card key={p.id} className={`rounded-2xl p-6 border ${p.tier==="individual"?"border-teal ring-1 ring-teal":"border-teal/15"}`} data-testid={`plan-${p.id}`}>
              <div className="text-xs uppercase tracking-widest text-teal font-bold">{tierLabel}</div>
              <h3 className="font-display text-2xl mt-1 mb-3">{planLabel}</h3>
              {discountAmt > 0 && <div className="text-xs text-muted-foreground line-through mb-0.5">{formatIDR(p.amount)}</div>}
              <div className="font-display text-3xl font-medium mb-1">
                {formatIDR(finalAmt)}<span className="text-base text-muted-foreground font-normal">/{yearly ? t("pricing.per_year") : t("pricing.per_month")}</span>
              </div>
              <div className="text-xs text-muted-foreground">≈ ${p.usd} USD {discountAmt > 0 && <span className="text-teal font-semibold ml-1">−{formatIDR(discountAmt)}</span>}</div>
              <ul className="mt-5 space-y-2 text-sm">
                {(Array.isArray(t(`pricing_extra.features.${p.tier}`)) ? t(`pricing_extra.features.${p.tier}`) : []).map(h => (<li key={h} className="flex items-start gap-2"><Check className="w-4 h-4 text-teal mt-0.5"/>{h}</li>))}
              </ul>
              <Button onClick={()=>buy(p.id)} disabled={loadingId===p.id} className="w-full mt-6 rounded-full bg-teal hover:bg-teal/90 text-white" data-testid={`buy-${p.id}`}>{loadingId===p.id?t("pricing.processing"):t("pricing.subscribe")}</Button>
            </Card>
          );
        })}
      </div>
      <p className="text-xs text-center text-muted-foreground mt-6">{t("pricing_extra.test_card")}</p>
    </div>
  );
}
